import {
  AcceptResult,
  FleetOptimization,
  FleetOptimizationInput,
  OptimizedPlan,
  OptimizedRoute,
  OptimizedStop,
} from "../../../models";

const getStop = (data: any): OptimizedStop => ({
  type: data.type,
  id: String(data.id),
  lat: Number(data.lat ?? 0),
  lng: Number(data.lng ?? 0),
  legMeters: Number(data.leg_meters ?? 0),
  legSeconds: Number(data.leg_seconds ?? 0),
  serviceSeconds: Number(data.service_seconds ?? 0),
  arrivalSeconds: Number(data.arrival_seconds ?? 0),
  departureSeconds: Number(data.departure_seconds ?? 0),
  eta: data.eta ?? "",
  ...(data.note ? { note: String(data.note) } : {}),
  ...(data.fee != null && Number(data.fee) > 0
    ? { dumpFee: Number(data.fee) }
    : {}),
  ...(data.inventory_before ? { inventoryBefore: countMap(data.inventory_before) } : {}),
  ...(data.inventory_after ? { inventoryAfter: countMap(data.inventory_after) } : {}),
});

// A backend size->count map ({ "10": 3 }) coerced to numbers.
const countMap = (m: any): Record<string, number> => {
  const out: Record<string, number> = {};
  Object.keys(m ?? {}).forEach((k) => (out[k] = Number(m[k] ?? 0)));
  return out;
};

const getInfeasibility = (data: any) => ({
  stopId: String(data.stop_id),
  stopType: data.stop_type,
  ...(data.needed_size ? { neededSize: String(data.needed_size) } : {}),
  ...(data.waste_type ? { wasteType: String(data.waste_type) } : {}),
  detail: String(data.detail ?? ""),
  storages: (data.storages ?? []).map((s: any) => ({
    id: String(s.id),
    counts: countMap(s.counts),
  })),
});

const getCostBreakdown = (data: any) => ({
  fuelCost: Number(data.fuel_cost),
  wageCost: Number(data.wage_cost),
  dumpFees: Number(data.dump_fees),
  maintenanceCost: Number(data.maintenance_cost ?? 0),
  penalties: Array.isArray(data.penalties)
    ? data.penalties.map((p: any) => ({
        type: String(p.type),
        amount: Number(p.amount ?? 0),
      }))
    : [],
  cost: Number(data.cost),
  meters: Number(data.meters),
  drivingSeconds: Number(data.driving_seconds),
  serviceSeconds: Number(data.service_seconds),
  totalSeconds: Number(data.total_seconds),
});

const getRoute = (data: any): OptimizedRoute => ({
  ...getCostBreakdown(data),
  driverId: data.driver_id,
  vehicleId: data.vehicle_id,
  feasible: data.feasible,
  stops: (data.stops ?? []).map(getStop),
  ...(data.infeasibility ? { infeasibility: getInfeasibility(data.infeasibility) } : {}),
});

const getPlan = (data: any): OptimizedPlan => ({
  ...getCostBreakdown(data),
  routes: (data.routes ?? []).map(getRoute),
});

const getInput = (data: any): FleetOptimizationInput => ({
  date: data.date,
  driverAssignments: (data.driver_assignments ?? []).map((a: any) => ({
    driverId: a.driver_id,
    vehicleId: a.vehicle_id,
    startingDumpster: a.starting_dumpster ?? undefined,
    ...(a.start_time ? { startTime: String(a.start_time) } : {}),
    ...(a.end_time ? { endTime: String(a.end_time) } : {}),
  })),
  ...(data.end_time_penalty != null
    ? { endTimePenalty: Number(data.end_time_penalty) }
    : {}),
  ...(data.dump_fee_rank_weight != null
    ? { dumpFeeRankWeight: Number(data.dump_fee_rank_weight) }
    : {}),
  ...(data.compactness_penalty != null
    ? { compactnessPenalty: Number(data.compactness_penalty) }
    : {}),
  ...(data.maintenance_per_mile != null
    ? { maintenancePerMile: Number(data.maintenance_per_mile) }
    : {}),
  ...(data.time_window_penalty != null
    ? { timeWindowPenalty: Number(data.time_window_penalty) }
    : {}),
  ...(Array.isArray(data.time_windows)
    ? {
        timeWindows: data.time_windows.map((w: any) => ({
          jobId: String(w.job_id),
          start: String(w.start),
          end: String(w.end),
        })),
      }
    : {}),
  ...(data.overtime_allowed != null
    ? { overtimeAllowed: !!data.overtime_allowed }
    : {}),
  ...(data.overtime_penalty != null
    ? { overtimePenalty: Number(data.overtime_penalty) }
    : {}),
  ...(data.allow_carryover != null
    ? { allowCarryover: !!data.allow_carryover }
    : {}),
  ...(data.carryover_threshold != null
    ? { carryoverThreshold: Number(data.carryover_threshold) }
    : {}),
  ...(data.max_carryovers != null
    ? { maxCarryovers: Number(data.max_carryovers) }
    : {}),
  ...(data.balance_penalty != null
    ? { balancePenalty: Number(data.balance_penalty) }
    : {}),
  ...(Array.isArray(data.pickup_waste_types)
    ? {
        pickupWasteTypes: data.pickup_waste_types.map((w: any) => ({
          pickupId: String(w.pickup_id),
          wasteType: String(w.waste_type),
        })),
      }
    : {}),
  ...(Array.isArray(data.pickup_dump_weights)
    ? {
        pickupDumpWeights: data.pickup_dump_weights.map((w: any) => ({
          pickupId: String(w.pickup_id),
          weight: Number(w.weight),
        })),
      }
    : {}),
  ...(Array.isArray(data.pickup_dump_yards)
    ? {
        pickupDumpYards: data.pickup_dump_yards.map((y: any) => ({
          pickupId: String(y.pickup_id),
          dumpYardId: String(y.dump_yard_id),
        })),
      }
    : {}),
  ...(Array.isArray(data.locks)
    ? {
        locks: data.locks.map((l: any) => ({
          jobId: String(l.job_id),
          driverId: String(l.driver_id),
        })),
      }
    : {}),
  ...(Array.isArray(data.exclude)
    ? { exclude: data.exclude.map((id: any) => String(id)) }
    : {}),
  ...(Array.isArray(data.allow_topoff_for_jobs)
    ? { allowTopoffForJobs: data.allow_topoff_for_jobs.map((id: any) => String(id)) }
    : {}),
  ...(Array.isArray(data.precedences)
    ? {
        precedences: data.precedences.map((p: any) => ({
          afterJobId: String(p.after_job_id),
          beforeJobId: String(p.before_job_id),
        })),
      }
    : {}),
  ...(Array.isArray(data.swaps)
    ? {
        swaps: data.swaps.map((s: any) => ({
          pickupJobId: String(s.pickup_job_id),
          deliveryJobId: String(s.delivery_job_id),
        })),
      }
    : {}),
});

export const getAcceptResult = (data: any): AcceptResult => ({
  fleetOptimizationId: String(data.fleet_optimization_id ?? ""),
  date: String(data.date ?? ""),
  planIndex: Number(data.plan_index ?? 0),
  dryRun: !!data.dry_run,
  schedules: (data.schedules ?? []).map((s: any) => ({
    driverId: String(s.driver_id),
    ...(s.schedule_id ? { scheduleId: String(s.schedule_id) } : {}),
    scheduleCreated: !!s.schedule_created,
    clearedItems: Number(s.cleared_items ?? 0),
    deliveries: Number(s.deliveries ?? 0),
    pickups: Number(s.pickups ?? 0),
    dumpStorageStops: Number(s.dump_storage_stops ?? 0),
  })),
});

export const getFleetOptimization = (data: any): FleetOptimization => ({
  id: data.id,
  date: data.date,
  status: data.status,
  createdAt: data.created_at,
  input: getInput(data.input),
  result: data.result != null
    ? {
        options: (data.result.options ?? []).map(getPlan),
        ...(data.result.solve
          ? {
              solve: {
                durationMs: Number(data.result.solve.duration_ms ?? 0),
                workers: Number(data.result.solve.workers ?? 0),
                totalIterations: Number(data.result.solve.total_iterations ?? 0),
                solveBudgetMs: Number(data.result.solve.solve_budget_ms ?? 0),
              },
            }
          : {}),
      }
    : undefined,
  error: data.error ?? undefined,
});
